/**
 * Correcting one ball, and everything that follows from it.
 *
 * A `.panel` in the bottom half, never a floating modal — the house rule is that an overlay
 * would hide the score behind the dialog about the score. Same two-step-in-one-panel shape as
 * `WicketSheet`: a menu of what is wrong, then the one control that fixes it.
 *
 * **Every action here is ≤3 taps from the chip**, which is the acceptance criterion that
 * shaped the layout. Tap the ball, tap "different bowler for this over", tap the bowler:
 * three, and an entire over's wickets, economy, maidens and spells move.
 *
 * Nothing in this file computes a cricket number. It gathers an intent and calls
 * `corrections.ts`, which returns events; the scorecard comes back out of `replay()`. The
 * sheet does not even know whether the correction it just asked for changed the score.
 */
import { useState } from "react";
import { battingHandName, bowlingStyleName, formatBallChip, zoneOf } from "@sportsos/engine";
import type { BatterHand, BowlingArm, BowlingType, OverStripEntry, PlayerId, Shot } from "@sportsos/engine";
import { latchOf, type Latch } from "../match/entry.ts";
import type { SquadPlayer } from "../match/setup.ts";
import type { KnownBowling } from "../match/styles.ts";
import { BowlingStep, HandStep } from "./StyleSteps.tsx";

/** What the scorer decided. The screen turns one of these into events. */
export type BallCorrection =
  | { readonly kind: "runs"; readonly latch: Latch; readonly runs: number }
  | { readonly kind: "void" }
  | { readonly kind: "swap_ends" }
  | { readonly kind: "reassign_over"; readonly bowlerId: PlayerId }
  | { readonly kind: "substitute"; readonly fromPlayerId: PlayerId; readonly toPlayerId: PlayerId }
  /** The batter stands the other way: his whole innings is re-read, so the screen warns first (ADR-0023). */
  | { readonly kind: "batting_hand"; readonly playerId: PlayerId; readonly hand: BatterHand }
  /** A new arm for the bowler's whole innings and/or a new type for this one over (ADR-0023). */
  | {
      readonly kind: "bowling_style";
      readonly bowlerId: PlayerId;
      readonly overNo: number;
      readonly arm?: BowlingArm;
      readonly type?: BowlingType;
    };

/** Which control is showing. `null` is the menu. */
type Step = null | "runs" | "reassign_over" | "substitute" | "hand" | "bowling";

interface Props {
  readonly ball: OverStripEntry;
  /** Who the ball names, for the substitution list and for the labels. */
  readonly striker: SquadPlayer | undefined;
  readonly nonStriker: SquadPlayer | undefined;
  readonly bowler: SquadPlayer | undefined;
  readonly hasWicket: boolean;
  /** The bowling side's XI, for reassigning the over. */
  readonly bowlers: readonly SquadPlayer[];
  /** Everyone who could be the person actually meant, for a substitution. */
  readonly squad: readonly SquadPlayer[];
  readonly onCorrect: (correction: BallCorrection) => void;
  readonly onClose: () => void;
  /** Where the ball went, when it was recorded (ADR-0022). */
  readonly shot?: Shot | null;
  /** Offer the direction row: the wheel is on for this match, or this ball already has one. */
  readonly canDirect?: boolean;
  /** Open the wheel on this ball. Absent where the screen has no wheel to open. */
  readonly onDirection?: () => void;
  /** How the bowler bowled the over this ball is in, as the ledger has it (ADR-0023). */
  readonly overStyle?: KnownBowling;
}

const RUN_OPTIONS = [0, 1, 2, 3, 4, 6] as const;

export function EditBallSheet({
  ball,
  striker,
  nonStriker,
  bowler,
  hasWicket,
  bowlers,
  squad,
  onCorrect,
  onClose,
  shot = null,
  canDirect = false,
  onDirection,
  overStyle = { arm: null, type: null },
}: Props): React.ReactElement {
  const [step, setStep] = useState<Step>(null);
  // Seeded from the ball rather than mirrored into state, the same way `WicketSheet` handles
  // its overrides: the stored delivery is the default and only a deliberate tap moves off it.
  const seeded = latchOf(ball.runs, hasWicket);
  const [latch, setLatch] = useState<Latch>(seeded.latch);
  const [runs, setRuns] = useState<number>(seeded.runs);
  const [substituting, setSubstituting] = useState<PlayerId | null>(null);

  const where = `Ov ${ball.over + 1}.${ball.ballInOver}`;

  const back = (
    <button className="latch-clear" onClick={() => setStep(null)}>
      back
    </button>
  );

  // ── Step 2: the runs ──────────────────────────────────────────────────────
  if (step === "runs") {
    return (
      <div className="stack">
        <div>
          <div className="ds-title">
            {where} — what actually happened {back}
          </div>
          <div className="ds-caption">
            Recorded as {formatBallChip(ball)}. Everything after this ball re-derives, including
            who was on strike.
          </div>
        </div>

        <div className="wrap">
          {(
            [
              ["wide", latch.extra === "wide"],
              ["noball", latch.extra === "noball"],
            ] as const
          ).map(([kind, on]) => (
            <button
              key={kind}
              className="ds-chip"
              aria-pressed={on}
              onClick={() =>
                setLatch((current) => ({
                  ...current,
                  extra: current.extra === kind ? null : kind,
                }))
              }
            >
              <span>{kind === "wide" ? "Wide" : "No-ball"}</span>
            </button>
          ))}
          {(
            [
              ["bye", latch.bye === "bye"],
              ["legbye", latch.bye === "legbye"],
            ] as const
          ).map(([kind, on]) => (
            <button
              key={kind}
              className="ds-chip"
              aria-pressed={on}
              onClick={() =>
                setLatch((current) => ({ ...current, bye: current.bye === kind ? null : kind }))
              }
            >
              <span>{kind === "bye" ? "Byes" : "Leg byes"}</span>
            </button>
          ))}
        </div>

        <div className="ds-list" style={{ marginTop: 8 }}>
          {RUN_OPTIONS.map((option) => (
            <button
              key={option}
              className="ds-list-item"
              aria-selected={runs === option}
              onClick={() => setRuns(option)}
            >
              <span>
                {option} {option === 1 ? "run" : "runs"}
              </span>
            </button>
          ))}
        </div>

        <div className="actions">
          <button onClick={onClose}>Cancel</button>
          <button
            className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
            onClick={() => onCorrect({ kind: "runs", latch, runs })}
          >
            Save this ball
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: the over's bowler ─────────────────────────────────────────────
  if (step === "reassign_over") {
    return (
      <div className="stack">
        <div>
          <div className="ds-title">
            Who bowled over {ball.over + 1}? {back}
          </div>
          <div className="ds-caption">
            Every ball of the over moves. Wickets, runs, maidens and economy follow the bowler
            — they are not stored, so there is nothing to patch.
          </div>
        </div>
        <div className="ds-list">
          {bowlers.map((candidate) => (
            <button
              key={candidate.id}
              className="ds-list-item"
              disabled={candidate.id === bowler?.id}
              onClick={() => onCorrect({ kind: "reassign_over", bowlerId: candidate.id })}
            >
              <span className="ds-list-item__title">{candidate.name}</span>
              {candidate.id === bowler?.id ? <span className="ds-list-item__meta">bowled it</span> : null}
            </button>
          ))}
        </div>
        <div className="actions">
          <button onClick={onClose}>Cancel</button>
          <span />
        </div>
      </div>
    );
  }

  // ── Step 2 and 3: who it actually was ─────────────────────────────────────
  if (step === "substitute") {
    const from = substituting;
    if (from === null) {
      const named = [striker, nonStriker, bowler].filter(
        (person): person is SquadPlayer => person !== undefined,
      );
      return (
        <div className="stack">
          <div>
            <div className="ds-title">
              {where} — who was misidentified? {back}
            </div>
            <div className="ds-caption">
              Applied from where this player came in, across the rest of the innings — not just
              this ball.
            </div>
          </div>
          <div className="ds-list">
            {named.map((person) => (
              <button key={person.id} className="ds-list-item" onClick={() => setSubstituting(person.id)}>
                <span className="ds-list-item__title">{person.name}</span>
              </button>
            ))}
          </div>
          <div className="actions">
            <button onClick={onClose}>Cancel</button>
            <span />
          </div>
        </div>
      );
    }

    return (
      <div className="stack">
        <div>
          <div className="ds-title">
            This was actually…{" "}
            <button className="latch-clear" onClick={() => setSubstituting(null)}>
              change
            </button>
          </div>
          <div className="ds-caption">
            Everything {squad.find((p) => p.id === from)?.name ?? "he"} is credited with from
            that point becomes theirs.
          </div>
        </div>
        <div className="ds-list">
          {squad
            .filter((person) => person.id !== from)
            .map((person) => (
              <button
                key={person.id}
                className="ds-list-item"
                onClick={() =>
                  onCorrect({ kind: "substitute", fromPlayerId: from, toPlayerId: person.id })
                }
              >
                <span className="ds-list-item__title">{person.name}</span>
              </button>
            ))}
        </div>
        <div className="actions">
          <button onClick={onClose}>Cancel</button>
          <span />
        </div>
      </div>
    );
  }

  // ── Hand and bowling style (ADR-0023) ─────────────────────────────────────
  if (step === "hand") {
    return (
      <HandStep
        name={striker?.name ?? "the batter"}
        current={ball.batterHand}
        onPick={(hand) => onCorrect({ kind: "batting_hand", playerId: ball.batterId, hand })}
        onBack={() => setStep(null)}
      />
    );
  }

  if (step === "bowling" && bowler !== undefined) {
    return (
      <BowlingStep
        name={bowler.name}
        current={overStyle}
        doneLabel="Save"
        onDone={(choice) => {
          // Only what changed. An unchanged arm is not rewritten across the innings, and an
          // unchanged type is not rewritten on the over, so choosing what is there is a no-op.
          const arm = choice.arm === overStyle.arm ? undefined : choice.arm;
          const type = choice.type === overStyle.type ? undefined : choice.type;
          if (arm === undefined && type === undefined) {
            setStep(null);
            return;
          }
          onCorrect({
            kind: "bowling_style",
            bowlerId: bowler.id,
            overNo: ball.over,
            ...(arm === undefined ? {} : { arm }),
            ...(type === undefined ? {} : { type }),
          });
        }}
        onBack={() => setStep(null)}
      />
    );
  }

  // ── Step 1: what is wrong with this ball ──────────────────────────────────
  return (
    <div className="stack">
      <div>
        <div className="ds-title">{where} — what needs fixing?</div>
        <div className="ds-caption">
          Recorded as {formatBallChip(ball)}
          {bowler === undefined ? "" : `, bowled by ${bowler.name}`}
          {striker === undefined ? "" : `, faced by ${striker.name}`}.
        </div>
        {/* An anomaly is the reason a match cannot be completed, so it is stated here rather
            than left as a coloured dot the scorer has to interpret (ADR-0011). */}
        {ball.anomaly === null ? null : <div className="ds-hint ds-hint--error">{ball.anomaly}</div>}
        {ball.edited === null ? null : (
          <div className="ds-caption">
            Already edited {ball.edited.revisions === 1 ? "once" : `${ball.edited.revisions} times`}.
          </div>
        )}
      </div>

      <div className="ds-list">
        <button className="ds-list-item" onClick={() => setStep("runs")}>
          <span>The runs or the extras</span>
          <span className="why">re-records this ball</span>
        </button>
        {/* Only for a ball the batter scored off: a dot, a wide or a bye has no direction to give. */}
        {canDirect && onDirection !== undefined && ball.runs.batter > 0 ? (
          <button className="ds-list-item" onClick={onDirection}>
            <span>
              {shot === null
                ? "Where it went"
                : ball.batterHand === null
                  ? "Direction recorded"
                  : `Direction: ${zoneOf(shot.wagonAngle, ball.batterHand)}`}
            </span>
            <span className="why">{shot === null ? "add the wagon wheel" : "change it"}</span>
          </button>
        ) : null}
        <button className="ds-list-item" onClick={() => setStep("hand")}>
          <span>Which way {striker?.name ?? "the batter"} bats</span>
          <span className="why">{ball.batterHand === null ? "not recorded" : battingHandName(ball.batterHand)}</span>
        </button>
        {bowler === undefined ? null : (
          <button className="ds-list-item" onClick={() => setStep("bowling")}>
            <span>How {bowler.name} bowled this over</span>
            <span className="why">{bowlingStyleName(overStyle.arm, overStyle.type) ?? "not recorded"}</span>
          </button>
        )}
        <button className="ds-list-item" onClick={() => setStep("reassign_over")}>
          <span>A different bowler for this over</span>
          <span className="why">moves the whole over</span>
        </button>
        <button className="ds-list-item" onClick={() => onCorrect({ kind: "swap_ends" })}>
          <span>The batters were the other way round</span>
          <span className="why">from this ball on</span>
        </button>
        <button className="ds-list-item" onClick={() => setStep("substitute")}>
          <span>This was actually a different player</span>
          <span className="why">across the innings</span>
        </button>
        <button className="ds-list-item" onClick={() => onCorrect({ kind: "void" })}>
          <span>This ball never happened</span>
          <span className="why">dead ball recorded in error</span>
        </button>
      </div>

      <div className="actions">
        <button onClick={onClose}>Close</button>
        <span />
      </div>
    </div>
  );
}
